import { clsx } from 'clsx'

export interface CheckboxOption {
  id: string
  label: string
  children?: CheckboxOption[]
}

export interface CheckboxGroupProps {
  options: CheckboxOption[]
  value: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
  error?: string
  className?: string
  columns?: 1 | 2 | 3
}

export function CheckboxGroup({
  options,
  value,
  onChange,
  disabled = false,
  error,
  className,
  columns = 1,
}: CheckboxGroupProps) {
  const handleChange = (optionId: string, checked: boolean) => {
    if (checked) {
      onChange([...value, optionId])
    } else {
      // When unchecking a parent, also uncheck all children
      const option = findOption(options, optionId)
      const childIds = option?.children?.map((c) => c.id) || []
      onChange(value.filter((id) => id !== optionId && !childIds.includes(id)))
    }
  }

  const findOption = (opts: CheckboxOption[], id: string): CheckboxOption | undefined => {
    for (const opt of opts) {
      if (opt.id === id) return opt
      if (opt.children) {
        const found = findOption(opt.children, id)
        if (found) return found
      }
    }
    return undefined
  }

  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  }

  return (
    <div className={className}>
      <div className={clsx('grid gap-2', gridCols[columns])}>
        {options.map((option) => (
          <CheckboxItem
            key={option.id}
            option={option}
            value={value}
            onChange={handleChange}
            disabled={disabled}
          />
        ))}
      </div>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  )
}

interface CheckboxItemProps {
  option: CheckboxOption
  value: string[]
  onChange: (id: string, checked: boolean) => void
  disabled: boolean
  indent?: boolean
}

function CheckboxItem({ option, value, onChange, disabled, indent = false }: CheckboxItemProps) {
  const isChecked = value.includes(option.id)
  const hasChildren = option.children && option.children.length > 0

  return (
    <div className={clsx(indent && 'ml-6')}>
      <label
        className={clsx(
          'flex items-center gap-2 cursor-pointer',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <input
          type="checkbox"
          checked={isChecked}
          onChange={(e) => onChange(option.id, e.target.checked)}
          disabled={disabled}
          className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-secondary-700">{option.label}</span>
      </label>

      {/* Render children only when parent is checked */}
      {hasChildren && isChecked && (
        <div className="mt-2 space-y-2">
          {option.children!.map((child) => (
            <CheckboxItem
              key={child.id}
              option={child}
              value={value}
              onChange={onChange}
              disabled={disabled}
              indent
            />
          ))}
        </div>
      )}
    </div>
  )
}
